import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Product, formatEur } from '@/lib/index';

interface NetCostChartProps {
  netP1: number;
  netP2: number;
}

export function NetCostChart({ netP1, netP2 }: NetCostChartProps) {
  const data = [
    {
      name: 'Coste Neto',
      p1: netP1,
      p2: netP2
    }
  ];

  const tickFormatter = (v: number) => {
    return v >= 1000 ? (v / 1000).toFixed(0) + 'K' : v.toFixed(0);
  };

  return (
    <ResponsiveContainer width="100%" height={320}>
      <BarChart data={data} margin={{ top: 20, right: 30, left: 80, bottom: 20 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
        <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" />
        <YAxis stroke="hsl(var(--muted-foreground))" tickFormatter={tickFormatter} />
        <Tooltip
          content={({ active, payload }) => {
            if (active && payload && payload.length) {
              return (
                <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
                  {payload.map((entry, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-sm"
                        style={{ backgroundColor: entry.color }}
                      />
                      <span className="text-sm font-medium">
                        {entry.name}: {formatEur(entry.value as number)}
                      </span>
                    </div>
                  ))}
                </div>
              );
            }
            return null;
          }}
        />
        <Legend />
        <Bar dataKey="p1" fill="#22c55e" name="Coste Neto P1" radius={[8, 8, 0, 0]} />
        <Bar dataKey="p2" fill="#ef4444" name="Coste Neto P2" radius={[8, 8, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

interface GrossCostByProductChartProps {
  products: Product[];
}

export function GrossCostByProductChart({ products }: GrossCostByProductChartProps) {
  const data = products.map((product) => {
    const p1Cost = product.units * product.priceP1;
    const p2Cost = product.units * product.priceP2;
    
    const shortName = product.name.length > 20 
      ? product.name.substring(0, 17) + '...'
      : product.name;
    
    return {
      name: shortName,
      p1: p1Cost,
      p2: p2Cost
    };
  });

  const tickFormatter = (v: number) => {
    return v >= 1000 ? (v / 1000).toFixed(0) + 'K' : v.toFixed(0);
  };

  return (
    <ResponsiveContainer width="100%" height={320}>
      <BarChart data={data} margin={{ top: 20, right: 30, left: 80, bottom: 80 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
        <XAxis
          dataKey="name"
          angle={-20}
          textAnchor="end"
          height={80}
          interval={0}
          stroke="hsl(var(--muted-foreground))"
          tick={{ fontSize: 12 }}
        />
        <YAxis stroke="hsl(var(--muted-foreground))" tickFormatter={tickFormatter} />
        <Tooltip
          content={({ active, payload }) => {
            if (active && payload && payload.length) {
              return (
                <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
                  <p className="text-sm font-semibold mb-2">{payload[0].payload.name}</p>
                  {payload.map((entry, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-sm"
                        style={{ backgroundColor: entry.color }}
                      />
                      <span className="text-sm font-medium">
                        {entry.name}: {formatEur(entry.value as number)}
                      </span>
                    </div>
                  ))}
                </div>
              );
            }
            return null;
          }}
        />
        <Legend />
        <Bar dataKey="p1" fill="#22c55e" name="Coste P1" radius={[8, 8, 0, 0]} />
        <Bar dataKey="p2" fill="#ef4444" name="Coste P2" radius={[8, 8, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}